import styled from 'styled-components';

const Wrap = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(3.5)};
  background: ${({ theme }) => theme.colors.status.warning}1a;
  border: 1px solid ${({ theme }) => theme.colors.status.warning}55;
  border-left: 3px solid ${({ theme }) => theme.colors.status.warning};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Title = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.status.warning};
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Desc = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const List = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Snippet = styled.span`
  display: inline-block;
  margin-top: 2px;
  padding: 2px 6px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export interface InjectionFlag {
  pattern: string;
  snippet: string;
  char_offset: number;
}

const LABELS: Record<string, string> = {
  ignore_previous_en: 'Попытка сбросить инструкции (англ.)',
  ignore_previous_ru: 'Попытка сбросить инструкции',
  disregard_en: 'Попытка проигнорировать предыдущее',
  forget_previous_en: 'Попытка «забыть» правила (англ.)',
  forget_previous_ru: 'Попытка «забыть» правила',
  role_change_en: 'Попытка сменить роль агента (англ.)',
  role_change_ru: 'Попытка сменить роль агента',
  act_as_en: 'Ролевая подмена (англ.)',
  pretend_ru: 'Просьба «притвориться»',
  imagine_ru: 'Просьба «представить»',
  system_prompt_marker: 'Поддельный system-маркер',
  assistant_prompt_marker: 'Поддельный assistant-маркер',
  fake_contract_marker: 'Поддельный маркер <<<CONTRACT>>>',
  fake_template_marker: 'Поддельный маркер <<<TEMPLATE>>>',
  fake_law_marker: 'Поддельный маркер <<<APPLICABLE_LAW>>>',
};

function label(pattern: string): string {
  return LABELS[pattern] ?? `Подозрительный паттерн: ${pattern}`;
}

interface Props {
  flags: InjectionFlag[];
}

/**
 * баннер о попытках prompt-injection.
 * Показывает юристу: в тексте договора/письма найдены подозрительные
 * последовательности, которые могли попытаться повлиять на LLM-анализ.
 * Анализ всё равно проведён по структурным правилам (служебные маркеры
 * нейтрализованы), но эти места стоит проверить вручную.
 */
export function InjectionWarning({ flags }: Props) {
  if (!flags || flags.length === 0) return null;
  // Уникальные паттерны в порядке появления.
  const seenPatterns = new Set<string>();
  const dedup = flags.filter((f) => {
    if (seenPatterns.has(f.pattern)) return false;
    seenPatterns.add(f.pattern);
    return true;
  });
  return (
    <Wrap>
      <Title>⚠ Защита от prompt injection · {flags.length} срабатываний</Title>
      <Desc>
        В тексте документа обнаружены последовательности, похожие на попытки повлиять на LLM-анализ
        (сменить роль агента, «отменить» предыдущие инструкции, подделать наш формат). Системные
        маркеры нейтрализованы перед передачей в модель — структура анализа не нарушена. Однако эти
        места стоит проверить юристу вручную, потому что они могут быть как технической атакой, так
        и намеренной уязвимостью в формулировках.
      </Desc>
      <List>
        {dedup.map((f) => (
          <li key={`${f.pattern}-${f.char_offset}`}>
            <strong>{label(f.pattern)}</strong>
            <div>
              <Snippet>{f.snippet}</Snippet>
            </div>
          </li>
        ))}
      </List>
    </Wrap>
  );
}
